/**
 * Global Property Table View Component
 * 
 * Enhanced table view with relationship indicators and links to
 * related properties and option catalogs.
 * 
 * Refactored to use hooks and components for better separation of concerns.
 */

import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Row, Col, Card, Typography, Button, Space, Spin } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import { GlobalPropertyDetailsModal } from './GlobalPropertyDetailsModal';
import { TransitionChangeEditor } from './TransitionChangeEditor';
import { StateActionDialog } from './StateActionDialog';
import { PropertyTable } from './PropertyTable';
import { PropertyDetailsForm } from './PropertyDetailsForm';
import { useGlobalPropertyTable } from '../../hooks/useGlobalPropertyTable';
import { usePropertyDetailsForm } from '../../hooks/usePropertyDetailsForm';
import { convertStateActionsToArray } from '../../utils/globalPropertyUtils';
import { TransitionChange } from '../../types/globalProperties';
import styles from './GlobalPropertyTableView.module.css';

const { Title, Text } = Typography;

interface GlobalPropertyTableViewProps {
  propertyKeys?: string[];
  onPropertySelect?: (propertyKey: string) => void;
  onEditProperty?: (propertyKey: string) => void;
  showSplitPane?: boolean;
}

export interface GlobalPropertyTableViewRef {
  createAndNavigateToProperty: (propertyKey: string) => Promise<void>;
}

export const GlobalPropertyTableView = forwardRef<GlobalPropertyTableViewRef, GlobalPropertyTableViewProps>(({
  propertyKeys,
  onPropertySelect,
  onEditProperty,
  showSplitPane = false
}, ref) => {
  const [selectedPropertyKey, setSelectedPropertyKey] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Table hook
  const {
    loading,
    searchText,
    setSearchText,
    tableData,
    filteredData,
    columns,
    loadProperties,
    handleFormSuccess
  } = useGlobalPropertyTable({
    propertyKeys,
    onRowClick: (record) => {
      setSelectedPropertyKey(record.property_key);
      if (showSplitPane) {
        setShowEditForm(true);
      }
      onPropertySelect?.(record.property_key);
    },
    onPropertyKeyClick: (propertyKey) => {
      setSelectedPropertyKey(propertyKey);
      if (showSplitPane) {
        setShowEditForm(true);
      }
      onPropertySelect?.(propertyKey);
    },
    selectedPropertyKey,
    showSplitPane
  });

  // Form hook
  const {
    detailsForm,
    detailsLoading,
    optionCatalogs,
    detailsPropertyType,
    stateActions,
    localStateActions,
    localTransitionChanges,
    editingTransitionChange,
    showTransitionChangeEditor,
    changeTypeDefinitions,
    localChangeTypeDefinitions,
    selectedChangeType,
    calculationDependencies,
    validationRules,
    improvementPatterns,
    stateActionDialog,
    setDetailsPropertyType,
    setLocalStateActions,
    setSelectedChangeType,
    setLocalChangeTypeDefinitions,
    setLocalTransitionChanges,
    setEditingTransitionChange,
    setShowTransitionChangeEditor,
    setStateActionDialog,
    loadPropertyDefinitionForDetails,
    handleDetailsFormSubmit,
    handleSaveStateActions,
    buildStateActionDetails,
    handleDeleteChangeType,
    handleStateClick,
    ensureRequiredConfiguration,
    handleStateActionDialogSave
  } = usePropertyDetailsForm({
    selectedPropertyKey,
    showEditForm,
    onPropertySelect: (propertyKey) => {
      setSelectedPropertyKey(propertyKey);
      onPropertySelect?.(propertyKey);
    },
    onFormSuccess: () => {
      handleFormSuccess();
      loadProperties();
    },
    onCloseForm: () => {
      setShowEditForm(false);
      detailsForm.resetFields();
    }
  });

  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    createAndNavigateToProperty: async (propertyKey: string) => {
      await loadProperties();
      setSelectedPropertyKey(propertyKey);
      setShowEditForm(true);
    }
  }), [loadProperties]);

  return (
    <>
      {showSplitPane ? (
        <Row gutter={16} style={{ height: '100%' }}>
          {/* Left Pane - Properties List */}
          <Col xs={24} lg={12} xl={12}>
            <PropertyTable
              columns={columns}
              dataSource={filteredData}
              loading={loading}
              searchText={searchText}
              onSearchChange={setSearchText}
              onRefresh={loadProperties}
              onRowClick={(record) => {
                setSelectedPropertyKey(record.property_key);
                setShowEditForm(true);
                onPropertySelect?.(record.property_key);
              }}
              selectedPropertyKey={selectedPropertyKey}
              showSplitPane={true}
            />
          </Col>

          {/* Right Pane - Property Details/Edit Form */}
          <Col xs={24} lg={12} xl={12}>
            <Card
              title={
                <Title level={4} style={{ margin: 0 }}>
                  {selectedPropertyKey ? `Edit Property: ${selectedPropertyKey}` : 'Select a Property'}
                </Title>
              }
              className={styles.propertyDetailsCard}
            >
              {selectedPropertyKey && showEditForm ? (
                <Spin spinning={detailsLoading}>
                  <PropertyDetailsForm
                    form={detailsForm}
                    loading={detailsLoading}
                    propertyType={detailsPropertyType}
                    optionCatalogs={optionCatalogs}
                    stateActions={stateActions}
                    localStateActions={localStateActions}
                    changeTypeDefinitions={changeTypeDefinitions}
                    localChangeTypeDefinitions={localChangeTypeDefinitions}
                    selectedChangeType={selectedChangeType}
                    calculationDependencies={calculationDependencies}
                    validationRules={validationRules}
                    improvementPatterns={improvementPatterns}
                    onPropertyTypeChange={setDetailsPropertyType}
                    onStateActionsChange={setLocalStateActions}
                    onSaveStateActions={handleSaveStateActions}
                    onStateClick={handleStateClick}
                    onDeleteChangeType={handleDeleteChangeType}
                    onSelectedChangeTypeChange={setSelectedChangeType}
                    onLocalChangeTypeDefinitionsChange={setLocalChangeTypeDefinitions}
                    ensureRequiredConfiguration={ensureRequiredConfiguration}
                    onCancel={() => {
                      setShowEditForm(false);
                      detailsForm.resetFields();
                    }}
                    onSubmit={handleDetailsFormSubmit}
                    buildStateActionDetails={buildStateActionDetails}
                  />
                </Spin>
              ) : !showEditForm && selectedPropertyKey ? (
                <Card title={`Property: ${selectedPropertyKey}`}>
                  <Text type="secondary">
                    Click "Edit" in the table or select a property to view/edit its details.
                  </Text>
                  <br />
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setShowEditForm(true)}
                    style={{ marginTop: 16 }}
                  >
                    Edit Property
                  </Button>
                </Card>
              ) : !selectedPropertyKey ? (
                <Card>
                  <Text type="secondary">
                    Please select a property from the list to view its details.
                  </Text>
                </Card>
              ) : null}
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <PropertyTable
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            searchText={searchText}
            onSearchChange={setSearchText}
            onRefresh={loadProperties}
            selectedPropertyKey={selectedPropertyKey}
            showSplitPane={false}
            title="Global Properties"
          />

          {showDetailsModal && selectedPropertyKey && (
            <GlobalPropertyDetailsModal
              propertyKey={selectedPropertyKey}
              visible={showDetailsModal}
              onClose={() => {
                setShowDetailsModal(false);
                setSelectedPropertyKey(null);
              }}
            />
          )}
        </>
      )}

      {/* Transition Change Editor Modal */}
      <TransitionChangeEditor
        visible={showTransitionChangeEditor}
        transitionChange={editingTransitionChange}
        stateActions={stateActions}
        propertyKey={selectedPropertyKey || ''}
        onSave={(change: TransitionChange) => {
          if (editingTransitionChange) {
            // Update existing - find by transition
            const index = localTransitionChanges.findIndex(
              tc => tc.transition === editingTransitionChange.transition
            );
            if (index >= 0) {
              const newChanges = [...localTransitionChanges];
              newChanges[index] = change;
              setLocalTransitionChanges(newChanges);
            }
          } else {
            // Add new
            setLocalTransitionChanges([...localTransitionChanges, change]);
          }
          setShowTransitionChangeEditor(false);
          setEditingTransitionChange(undefined);
        }}
        onCancel={() => {
          setShowTransitionChangeEditor(false);
          setEditingTransitionChange(undefined);
        }}
      />

      {/* State Action Dialog */}
      {stateActionDialog && (
        <StateActionDialog
          visible={stateActionDialog.visible}
          state={stateActionDialog.state}
          flowType={stateActionDialog.flowType}
          currentStateAction={stateActionDialog.currentStateAction}
          currentFlowDirection={stateActionDialog.currentFlowDirection}
          currentChangeTypeRef={stateActionDialog.currentChangeTypeRef}
          forwardAction={stateActionDialog.forwardAction}
          forwardChangeTypeRef={stateActionDialog.forwardChangeTypeRef}
          backwardAction={stateActionDialog.backwardAction}
          backwardChangeTypeRef={stateActionDialog.backwardChangeTypeRef}
          changeTypeDefinitions={[]}
          allStateActions={convertStateActionsToArray(localStateActions).map(entry => ({
            state: entry.state,
            flowType: entry.flow_type,
            stateActionType: entry.state_action_type
          }))}
          onSave={handleStateActionDialogSave}
          onCancel={() => setStateActionDialog(null)}
        />
      )}
    </>
  );
});
